import type { BlogPost } from "../blogTypes";

export const rentalContractChecklistBeforeSigningNetherlands: BlogPost = {
    slug: "rental-contract-checklist-before-signing-netherlands",
    title: "The Complete Pre-Signing Checklist for Dutch Rental Contracts",
    metaTitle: "Rental Contract Checklist Netherlands | Before You Sign 2026",
    metaDescription: "The complete checklist of clauses, documents and warning signs to verify before signing a Dutch rental contract, fact-checked against official 2026 rules.",
    excerpt: "A Dutch rental contract can affect your costs and housing security for years. This checklist helps you verify the main terms, documents and warning signs before signing.",
    date: "2025-09-08",
    updated: "2026-08-23",
    readTime: "6 min read",
    category: "Contract Review",
    keywords: ["Dutch rental contract checklist", "before signing rental Netherlands", "huurcontract verify", "rental agreement checklist Netherlands"],
    content: `
## Table of Contents

- [Why a Checklist Matters](#why-a-checklist-matters)
- [Section 1: Identity and Basics](#section-1-identity-and-basics)
- [Section 2: Rent and Costs](#section-2-rent-and-costs)
- [Section 3: Rent Increase Clauses](#section-3-rent-increase-clauses)
- [Section 4: Duration and Termination](#section-4-duration-and-termination)
- [Section 5: Deposit](#section-5-deposit)
- [Section 6: Maintenance](#section-6-maintenance)
- [Section 7: Access and Privacy](#section-7-access-and-privacy)
- [Section 8: Use of Property](#section-8-use-of-property)
- [Section 9: Documents to Collect](#section-9-documents-to-collect)
- [Section 10: Phrases Worth Clarifying](#section-10-phrases-worth-clarifying)
- [Section 11: After You Sign](#section-11-after-you-sign)
- [Sources Checked](#sources-checked)

## Why a Checklist Matters

Many rental contract disputes in the Netherlands trace back to a clause the tenant didn't notice before signing. By the time the problem surfaces — often at rent-increase time or at move-out — it can be harder to resolve. This checklist takes about 15 minutes to work through and helps you spot the terms worth clarifying before you sign.

## Section 1: Identity and Basics

- The **exact legal contracting party** is named — this can be a natural person or a legal entity (company); a company landlord is not itself a red flag
- **KvK number** (Chamber of Commerce) listed if the landlord is a business
- **Property address** matches exactly what you viewed
- **Square metres** stated in the contract match the listing
- **Move-in date** is correct and confirmed in writing
- For contracts entered into on or after 1 January 2025, the landlord must provide the home's **points calculation (puntentelling)**

Source: [Landlord information duties under the Good Landlordship Act](https://www.volkshuisvestingnederland.nl/onderwerpen/huren-en-wonen/wet-goed-verhuurderschap/landelijke-regels-van-goed-verhuurderschap)

## Section 2: Rent and Costs

- **Base rent (kale huur)** is stated separately from service costs
- **Service costs (servicekosten)** are itemized, not a lump sum
- **Utilities** are clearly marked as included or excluded
- **Total monthly cost** is the sum you expected
- **Payment due date** is specified (usually 1st of the month)
- **Payment method** is a traceable transfer to the account identified by the verified landlord or agent — a landlord generally cannot refuse a valid EU/EEA IBAN just because it isn't a Dutch one, as that can amount to IBAN discrimination; avoid cash mainly because it's hard to prove, not because it's automatically unlawful

Source: [DNB guidance on SEPA and IBAN discrimination](https://www.dnb.nl/betalen/hoe-werkt-betalen/sepa-en-iban-discriminatie/)

## Section 3: Rent Increase Clauses

- **Increase formula** is explicit (e.g., a reference to CPI or "the maximum legal percentage")
- **Increase date** is specified — "usually 1 July with at least two months' notice" is a feature of the **social-housing proposal procedure**, not a rule for every segment
- For **middle-rent and free-sector** contracts, check the contractual increase clause itself, the applicable 2026 cap, and that you can ask the Huurcommissie to assess a contractual increase within **four months** of the proposed effective date
- Clause is **not** vague ("rent may be adjusted from time to time" is worth querying)

Sources: [2026 rent-increase percentages](https://www.rijksoverheid.nl/actueel/nieuws/2025/12/15/maximale-huurverhoging-2026-in-sociale-sector-41-middenhuur-61-en-vrije-sector-44), [Huurcommissie free-sector increase assessment](https://www.huurcommissie.nl/onderwerpen/huurder-vrije-sector/huurverhoging-vrije-sector-huurder/jaarlijkse-huurverhoging-beoordelen)

## Section 4: Duration and Termination

- **Contract type** is clearly fixed-term or indefinite. Since 1 July 2024, an **indefinite contract is the norm** for new tenancies; an ordinary temporary contract is only valid if it fits a specific statutory exception
- **End date** is stated for fixed-term contracts, along with the exception it relies on
- **Notice period for tenant** normally follows the rent-payment interval — usually one month, with a statutory maximum of three months
- **Notice period for landlord** is typically three months, increasing by one month for each full year of tenancy up to a maximum of six months — and the landlord also needs a valid statutory termination ground
- **Diplomatic clause / return clause** is either absent or its conditions, trigger and the party who can invoke it are explicit
- **Minimum stay period**: there is no universal statutory cap (for example, no fixed 12-month maximum). Any minimum-stay clause should be reviewed individually rather than assumed to be reasonable or enforceable

Sources: [Temporary-contract rules](https://www.rijksoverheid.nl/vraag-en-antwoord/huurwoning-zoeken/wanneer-kan-ik-tijdelijk-huren), [Government termination guidance](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/verhuurder-zegt-huur-op-woning)

## Section 5: Deposit

- **Deposit amount** is no more than **two months' basic rent (kale huur)** for contracts entered into on or after 1 July 2023
- **Deposit return terms** reference both the **14-day** deadline (no deductions) and the **30-day** deadline (itemised deductions)
- **Inventory report (opnamerapport)** is mentioned and will be created jointly
- **Bank account** for deposit return is identified (yours)

Source: [Government deposit guidance](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/woning-huren-waarborgsom)

## Section 6: Maintenance

- **Major maintenance** is the landlord's responsibility (groot onderhoud)
- Minor repairs listed in the Besluit kleine herstellingen are generally the tenant's responsibility when they are accessible to the tenant and can be completed without significant cost. There is no single generic monetary cap for all minor repairs.
- The contract references the **Besluit kleine herstellingen**
- No clauses make you responsible for **structural repairs**
- **Garden maintenance** responsibility is clear if applicable

## Section 7: Access and Privacy

- The contract requires the landlord's **prior consent and a reasonably agreed time** for non-emergency visits — there is no universal statutory "24–48 hours" rule, so treat any stated number as a convention to negotiate, not a legal floor or ceiling
- There is **no statutory cap** on inspection frequency (e.g. no fixed "once or twice a year" rule) — instead check that visits require agreement rather than being at the landlord's sole discretion
- No clauses allow entry **"at any time"** or **"without notice"**
- Emergency access is defined narrowly (e.g. fire, flood, gas leak)

Source: [National good-landlordship rules](https://www.volkshuisvestingnederland.nl/onderwerpen/huren-en-wonen/wet-goed-verhuurderschap/landelijke-regels-van-goed-verhuurderschap)

## Section 8: Use of Property

- **Permitted use** matches your intent (residential, business, both)
- **Pets policy** is explicit if you have or plan pets
- **Smoking policy** is clear
- **Subletting** rules are explicit (full vs partial subletting)
- **Maximum occupants** is reasonable for the property size

## Section 9: Documents to Collect

- Signed copy of **both Dutch and English versions** (if bilingual)
- **Energy label (energielabel)** of the property
- The home's **points calculation**, for contracts entered into on or after 1 January 2025
- **Inventory report** with photos, signed by both parties
- **Keys handover receipt** listing every key
- **Meter readings** (gas, electricity, water) at move-in
- **Landlord's contact information** for emergencies

## Section 10: Phrases Worth Clarifying

Search your contract for these phrases. If you find them, ask for clarification before signing — their effect depends on context, so treat them as prompts to ask questions, not automatic legal conclusions:

- **"Naar inzicht van de verhuurder"** (at the landlord's discretion)
- **"Te allen tijde"** (at all times) — usually about access
- **"Voor rekening van huurder"** (at tenant's expense) — check what's covered
- **"Boete"** (penalty) — penalties for breaches should be reasonable
- **"Eindstaat zoals beginstaat"** (return in original state) — sets a demanding standard, verify against the move-in report
- **"Casco"** (shell only) — its practical effect depends on the specific contract and context; more common in commercial/shell arrangements, and does not automatically mean the tenant is responsible for everything

## Section 11: After You Sign

Even after signing:

- Take **dated photos** of every room soon after move-in
- Record **meter readings** with timestamps
- Save **all communication** with the landlord in writing
- Report your change of address to the **gemeente**: generally from four weeks before the move until no later than five days after it. If you are arriving from abroad to live in the Netherlands for more than four months, you generally need to register within five days of arrival. Check your municipality's appointment and document requirements.

Sources: [Government change-of-address window](https://www.rijksoverheid.nl/vraag-en-antwoord/gemeenten/hoe-kan-ik-mijn-verhuizing-doorgeven-aan-de-gemeente), [Registration after arrival in the Netherlands](https://www.government.nl/faq/when-should-i-register-with-the-personal-records-database-as-a-resident)

## Don't Sign Blind

This checklist catches common issues, but Dutch rental contracts can hide problematic clauses in dense legal language. [Upload your contract to RentSense NL](/app) for an analysis developed with reference to current official Dutch government and Huurcommissie sources, for €7.99 — it highlights clauses worth reviewing against current Dutch rental law before you sign. This is general information, not legal advice for your specific situation.

## Sources Checked

- [Landlord information duties under the Good Landlordship Act](https://www.volkshuisvestingnederland.nl/onderwerpen/huren-en-wonen/wet-goed-verhuurderschap/landelijke-regels-van-goed-verhuurderschap)
- [DNB guidance on SEPA and IBAN discrimination](https://www.dnb.nl/betalen/hoe-werkt-betalen/sepa-en-iban-discriminatie/)
- [2026 rent-increase percentages](https://www.rijksoverheid.nl/actueel/nieuws/2025/12/15/maximale-huurverhoging-2026-in-sociale-sector-41-middenhuur-61-en-vrije-sector-44)
- [Huurcommissie free-sector increase assessment](https://www.huurcommissie.nl/onderwerpen/huurder-vrije-sector/huurverhoging-vrije-sector-huurder/jaarlijkse-huurverhoging-beoordelen)
- [Temporary-contract rules](https://www.rijksoverheid.nl/vraag-en-antwoord/huurwoning-zoeken/wanneer-kan-ik-tijdelijk-huren)
- [Government termination guidance](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/verhuurder-zegt-huur-op-woning)
- [Government deposit guidance](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/woning-huren-waarborgsom)
- [National good-landlordship rules](https://www.volkshuisvestingnederland.nl/onderwerpen/huren-en-wonen/wet-goed-verhuurderschap/landelijke-regels-van-goed-verhuurderschap)
- [Government change-of-address window](https://www.rijksoverheid.nl/vraag-en-antwoord/gemeenten/hoe-kan-ik-mijn-verhuizing-doorgeven-aan-de-gemeente)
- [Registration after arrival in the Netherlands](https://www.government.nl/faq/when-should-i-register-with-the-personal-records-database-as-a-resident)
    `,
};
